import { useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { api } from '../../api/client'
import { useItem } from '../../api/queries'
import type { EquipmentDocument } from '../../api/types'
import { useUser } from '../../auth/AuthContext'
import { Icon } from '../../components/Icon'
import { Card, ErrorState, Field, LoadingState } from '../../components/ui'
import { can } from '../../lib/permissions'
import { EquipmentManualGuide } from './EquipmentManualGuide'

type MatchScope = 'exact' | 'family' | 'generic'

const MATCH_LABELS: Record<MatchScope, string> = {
  exact: 'Modelo exacto',
  family: 'Familia del equipo',
  generic: 'Referencia general',
}

const title = (manual: EquipmentDocument) => manual.title || manual.original_name || 'Manual técnico'

function formatBytes(bytes: number) {
  if (bytes === 0) return 'Enlace oficial'
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} kB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function ManualUpload({ path }: { path: string }) {
  const client = useQueryClient()
  const [file, setFile] = useState<File | null>(null)
  const [name, setName] = useState('')
  const [sourceUrl, setSourceUrl] = useState('')
  const [scope, setScope] = useState<MatchScope>('family')
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!file) return
    const formElement = event.currentTarget
    setBusy(true); setError(null)
    try {
      const form = new FormData()
      form.append('file', file)
      form.append('kind', 'manual')
      form.append('title', name.trim())
      form.append('source_url', sourceUrl.trim())
      form.append('match_scope', scope)
      form.append('match_note', note.trim())
      await api.upload<EquipmentDocument>(path, form)
      setFile(null); setName(''); setSourceUrl(''); setScope('family'); setNote('')
      formElement.reset()
      await client.invalidateQueries({ predicate: (q) => q.queryKey[0] === path })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'La carga falló.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <form className="manual-upload" onSubmit={submit}>
      <h3>Agregar manual</h3>
      <div className="manual-upload__grid">
        <Field label="PDF" htmlFor="manual-file"><input id="manual-file" type="file" accept="application/pdf,.pdf" onChange={(event) => setFile(event.target.files?.[0] ?? null)} /></Field>
        <Field label="Título" htmlFor="manual-title"><input id="manual-title" value={name} maxLength={200} placeholder="Ej. Manual SEW serie R" onChange={(event) => setName(event.target.value)} /></Field>
        <Field label="Coincidencia" htmlFor="manual-match"><select id="manual-match" value={scope} onChange={(event) => setScope(event.target.value as MatchScope)}>{Object.entries(MATCH_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></Field>
        <Field label="Fuente oficial" htmlFor="manual-source" hint="Enlace donde se obtuvo el documento."><input id="manual-source" type="url" value={sourceUrl} maxLength={500} placeholder="https://fabricante.com/manual.pdf" onChange={(event) => setSourceUrl(event.target.value)} /></Field>
      </div>
      <Field label="Aplicabilidad" htmlFor="manual-note" hint="Explica qué modelo o familia cubre."><input id="manual-note" value={note} maxLength={300} placeholder="Aplica a R77, R87, R107, R147 y R167" onChange={(event) => setNote(event.target.value)} /></Field>
      <button type="submit" className="btn btn--primary btn--sm" disabled={!file || !name.trim() || busy}><Icon name="upload" size={14} /> {busy ? 'Subiendo…' : 'Subir manual'}</button>
      {error ? <p role="alert" className="form-error">{error}</p> : null}
    </form>
  )
}

export function EquipmentManuals({ equipmentId }: { equipmentId: number }) {
  const user = useUser()
  const path = `/api/equipment/${equipmentId}/documents/`
  const docs = useItem<EquipmentDocument[]>(path)
  const manuals = (Array.isArray(docs.data) ? docs.data : []).filter((document) => document.kind === 'manual')

  return (
    <div className="stack">
    <EquipmentManualGuide equipmentId={equipmentId} />
    <div id="manual-documents" className="manual-guide__section">
    <Card title="Documentos y fuentes">
      <p className="muted manual-intro">Documentación de fabricante asociada por el modelo de placa. Revisa el nivel de coincidencia antes de usar valores o procedimientos.</p>
      {docs.isPending ? <LoadingState label="Cargando manuales…" /> : docs.isError ? <ErrorState error={docs.error} onRetry={() => void docs.refetch()} /> : manuals.length === 0 ? (
        <div className="empty"><Icon name="info" size={24} /><p>No se encontró todavía un manual verificable para este equipo.</p></div>
      ) : (
        <ul className="manual-list" aria-label="Manuales del equipo">
          {manuals.map((manual) => {
            const scope = (manual.match_scope || 'generic') as MatchScope
            return (
              <li key={manual.id} className="manual-card">
                <div className="manual-card__icon" aria-hidden="true"><Icon name="inspections" size={24} /></div>
                <div className="manual-card__body">
                  <div className="manual-card__heading"><strong>{title(manual)}</strong><span className="manual-match" data-scope={scope}>{MATCH_LABELS[scope]}</span></div>
                  {manual.match_note ? <p>{manual.match_note}</p> : null}
                  <small>{formatBytes(manual.size_bytes)} · PDF</small>
                  <div className="row">
                    <a className="btn btn--primary btn--sm" href={manual.url} target="_blank" rel="noopener noreferrer"><Icon name="download" size={14} /> Abrir PDF</a>
                    {manual.source_url && manual.source_url !== manual.url ? <a className="btn btn--ghost btn--sm" href={manual.source_url} target="_blank" rel="noopener noreferrer"><Icon name="arrow-up-right" size={14} /> Fuente oficial</a> : null}
                  </div>
                </div>
              </li>
            )
          })}
        </ul>
      )}
      {can(user.role, 'equipment.documents') ? <ManualUpload path={path} /> : null}
    </Card>
    </div>
    </div>
  )
}
